import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { Navigate, useNavigate } from 'react-router-dom'

import { AxiosError } from 'axios'
import { addMonths, format, getMonth, getYear, isValid, parse, parseISO, startOfMonth, subMonths } from 'date-fns'
import { isEmpty, isNil } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import {
  ProviderInsightsCompetencyCategory,
  ProviderInsightsSubAreaName,
} from '@lyrahealth-inc/shared-app-logic/src/features/careInsights/constants'
import { getSkillsGlossaryEntries } from '@lyrahealth-inc/shared-app-logic/src/features/careInsights/skillsGlossary'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { ProviderInsightsSummary } from '@lyrahealth-inc/shared-app-logic/src/models/careInsights/CareInsights'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { MonthYearDropdownMenu } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/monthYearDropdownMenu/MonthYearDropdownMenu'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { InlineWarningBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineWarningBanner/InlineWarningBanner'
import { SkillsGlossarySidePanel } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/skillsGlossarySidePanel/SkillsGlossarySidePanel'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { CareInsightsFeedbackSection } from './CareInsightsFeedbackSection'
import { CareInsightsOnboardingModal } from './CareInsightsOnboardingModal'
import { CareInsightsOverview } from './CareInsightsOverview'
import { CulturallyResponsiveCareInsights } from './CulturallyResponsiveCareInsights'
import { RiskManagementInsights } from './RiskManagementInsights'
import { SessionAdministrationInsights } from './SessionAdministrationInsights'
import { SupervisorUsageGuidelinesModal } from './SupervisorUsageGuidelinesModal'
import { TherapeuticRelationshipInsights } from './TherapeuticRelationshipInsights'
import { TreatmentPlanningInsights } from './TreatmentPlanningInsights'
import { actions, mixpanelEvents, mixpanelPages } from '../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { CLIENT_HOME } from '../../../common/constants/routingConstants'
import { getAppIsLdInitialized } from '../../../data/appSelectors'
import {
  getAuthIsLTSupervisor,
  getAuthSupervisorId,
  getAuthUser,
  getAuthUserId,
} from '../../../data/auth/authSelectors'
import { useAppDispatch } from '../../../data/store'
import { setSelectedClientId } from '../../clients/clientDetails/data/ltClientSlice'
import { useAllClientsData } from '../../clients/data/hooks/useAllClientsData'
import {
  useGetCareInsightsMetricsQuery,
  useGetCareInsightsOverviewQuery,
  useGetProviderAccessedCareInsightsPageQuery,
  useGetProviderInsightsQuery,
  useUpdateProviderAccessedCareInsightsPageMutation,
} from '../data/careInsightsApi'

const TitleContainer = styled.View(({ theme }) => ({
  padding: theme.spacing['24px'],
  backgroundColor: theme.colors.backgroundPrimary,
  borderBottomWidth: '1px',
  borderBottomColor: theme.colors.borderDefault,
  zIndex: 1,
}))

const ContentContainer = styled.View(({ theme }) => ({
  maxWidth: '944px',
  margin: '0 auto',
  gap: theme.spacing['24px'],
}))

const SubHeaderContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: theme.spacing['24px'],
}))

const MonthSelectorContainer = styled.View({
  flexDirection: 'row',
})

const InsightsContainer = styled.View(({ theme }) => ({
  marginTop: theme.spacing['8px'],
}))

export const CareInsights: React.FC = () => {
  const { formatMessage } = useIntl()
  const { isCareInsightsVisible, showRiskManagementInCareInsights, isSkillsGlossaryEnabled } = useFlags()
  const isLDReady = useSelector(getAppIsLdInitialized)
  const { colors } = useTheme()

  const dispatch = useAppDispatch()

  const userId = useSelector(getAuthUserId)
  const user = useSelector(getAuthUser)
  const isSupervisor = useSelector(getAuthIsLTSupervisor)
  const supervisorId = useSelector(getAuthSupervisorId)

  const pageViewerId = isSupervisor ? (supervisorId ?? '') : userId

  const [isOnboardingModalOpen, setIsOnboardingModalOpen] = useState(false)
  const [isSupervisorUsageGuidelinesModalOpen, setIsSupervisorUsageGuidelinesModalOpen] = useState(false)
  const [isSkillsGlossaryOpen, setIsSkillsGlossaryOpen] = useState(false)
  const [activeGlossarySkill, setActiveGlossarySkill] = useState<ProviderInsightsSubAreaName>()
  const skillsGlossaryEntries = useMemo(() => getSkillsGlossaryEntries(), [])

  const { data: accessedPageData, error: accessedPageError } = useGetProviderAccessedCareInsightsPageQuery(
    { id: pageViewerId },
    { skip: !isCareInsightsVisible || !pageViewerId },
  )
  const [updateProviderAccessedCareInsightsPage] = useUpdateProviderAccessedCareInsightsPageMutation()

  const hasHandledFirstVisit = useRef(false)

  const isAccessedPageNotFound = (accessedPageError as AxiosError)?.response?.status === 404

  useEffect(() => {
    if (!hasHandledFirstVisit.current && (accessedPageData === false || isAccessedPageNotFound)) {
      hasHandledFirstVisit.current = true
      if (isSupervisor) {
        setIsSupervisorUsageGuidelinesModalOpen(true)
      } else {
        setIsOnboardingModalOpen(true)
      }
      updateProviderAccessedCareInsightsPage({ id: pageViewerId, data: { value: true, value_type: 'boolean' } })
    }
  }, [accessedPageData, isAccessedPageNotFound, isSupervisor, pageViewerId, updateProviderAccessedCareInsightsPage])

  const [selectedMonthYear, setSelectedMonthYear] = useState(
    format(startOfMonth(subMonths(new Date(), 1)), 'MMMM yyyy'),
  )

  const { data: careOverviewInsights, isLoading: isLoadingCareOverviewInsights } = useGetCareInsightsOverviewQuery(
    {
      providerId: userId,
      currentDate: format(new Date(), 'yyyy-MM-dd'),
    },
    { skip: !isCareInsightsVisible },
  )

  const { data: careInsightsMetrics, isLoading: isLoadingCareInsightsMetrics } = useGetCareInsightsMetricsQuery(
    {
      providerId: userId,
      startDate: format(startOfMonth(parse(selectedMonthYear, 'MMMM yyyy', new Date())), 'yyyy-MM-dd'),
      endDate: format(startOfMonth(addMonths(parse(selectedMonthYear, 'MMMM yyyy', new Date()), 1)), 'yyyy-MM-dd'),
    },
    { skip: !isCareInsightsVisible },
  )

  const hasInitializedMonthYear = useRef(false)

  if (!hasInitializedMonthYear.current && careOverviewInsights?.length && !hasInitializedMonthYear.current) {
    hasInitializedMonthYear.current = true
    const mostRecent = careOverviewInsights.reduce((latest, current) =>
      parseISO(current.start_date) > parseISO(latest.start_date) ? current : latest,
    )
    setSelectedMonthYear(format(parseISO(mostRecent.start_date), 'MMMM yyyy'))
  }

  const selectedMonthDate = parse(selectedMonthYear, 'MMMM yyyy', new Date())
  const nextMonthDate = addMonths(selectedMonthDate, 1)
  const pastMonthDate = subMonths(selectedMonthDate, 1)

  const { data: providerInsightsCurrentMonth, isLoading: isLoadingProviderInsights } = useGetProviderInsightsQuery(
    {
      providerId: userId,
      startDate: {
        year: getYear(selectedMonthDate),
        month: getMonth(selectedMonthDate) + 1,
        day: 1,
      },
      endDate: {
        year: getYear(nextMonthDate),
        month: getMonth(nextMonthDate) + 1,
        day: 1,
      },
    },
    { skip: !isCareInsightsVisible },
  )

  const { data: providerInsightsPastMonth } = useGetProviderInsightsQuery(
    {
      providerId: userId,
      startDate: {
        year: getYear(pastMonthDate),
        month: getMonth(pastMonthDate) + 1,
        day: 1,
      },
      endDate: {
        year: getYear(selectedMonthDate),
        month: getMonth(selectedMonthDate) + 1,
        day: 1,
      },
    },
    { skip: !isSupervisor || !isCareInsightsVisible },
  )

  const [clients, { isLoading: isLoadingActiveClients }, { isLoading: isLoadingInactiveClients }] = useAllClientsData()

  const isLoadingClients = isLoadingActiveClients || isLoadingInactiveClients

  const navigate = useNavigate()

  const getClientInfoFromIds = useCallback(
    (clientIds: { value: string | number }[]) => {
      const validClients = clientIds.filter((clientId) => clients.find((c) => c.id === clientId.value))
      return validClients.map((clientId) => {
        const client = clients.find((c) => c.id === clientId.value)!
        return {
          name:
            client.first_name && client.last_name ? client.first_name + ' ' + client.last_name[0] : 'Unknown Client',
          clientId: clientId.value.toString(),
        }
      })
    },
    [clients],
  )

  const navigateToClientProfile = useCallback(
    (clientId: string) => {
      dispatch(setSelectedClientId(clientId))
      navigate(CLIENT_HOME.route)
    },
    [navigate, dispatch],
  )

  useEffect(() => {
    track({
      event: mixpanelEvents.OPEN_CARE_INSIGHTS_PAGE,
      page: mixpanelPages.CARE_INSIGHTS,
      properties: {
        month: format(parse(selectedMonthYear, 'MMMM yyyy', new Date()), 'yyyy-MM'),
        provider_id: userId,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCloseOnboardingModal = useCallback(() => {
    setIsOnboardingModalOpen(false)
  }, [])

  const openSkillsGlossary = useCallback((subAreaName?: ProviderInsightsSubAreaName) => {
    setActiveGlossarySkill(subAreaName)
    setIsSkillsGlossaryOpen(true)
  }, [])

  const closeSkillsGlossary = useCallback(() => {
    setIsSkillsGlossaryOpen(false)
    setActiveGlossarySkill(undefined)
  }, [])

  const handleCloseSupervisorUsageGuidelinesModal = useCallback(() => {
    setIsSupervisorUsageGuidelinesModalOpen(false)
  }, [])

  const { emptyClinicalQualitySectionCount, totalClinicalQualitySections } = useMemo(() => {
    const summary = providerInsightsCurrentMonth?.summary
    const competencyAreaNames: (keyof ProviderInsightsSummary)[] = [
      ProviderInsightsCompetencyCategory.ATTUNEMENT_AND_RAPPORT,
      ProviderInsightsCompetencyCategory.TREATMENT_PLANNING,
      ProviderInsightsCompetencyCategory.CULTURALLY_RESPONSIVE,
      ...(showRiskManagementInCareInsights
        ? ([ProviderInsightsCompetencyCategory.RISK_MANAGEMENT] as (keyof ProviderInsightsSummary)[])
        : []),
    ]
    const emptyCount = competencyAreaNames.filter((areaName) => {
      const section = summary?.[areaName]
      return isNil(section) || isEmpty(section)
    }).length
    return { emptyClinicalQualitySectionCount: emptyCount, totalClinicalQualitySections: competencyAreaNames.length }
  }, [providerInsightsCurrentMonth?.summary, showRiskManagementInCareInsights])

  if (!isLDReady) {
    return <LoadingIndicator />
  }

  if (!isCareInsightsVisible) {
    return <Navigate to='/not-found' />
  }

  if (isLoadingCareOverviewInsights) {
    return <LoadingIndicator />
  }

  if (isNil(careOverviewInsights) || careOverviewInsights.length === 0) {
    return (
      <TitleContainer testID={tID('CareInsights-titleContainer')}>
        <Title
          size={TitleSize.LARGE}
          text={formatMessage({
            defaultMessage: 'No care insights available',
            description: 'Message displayed when there are no care insights available',
          })}
        />
      </TitleContainer>
    )
  }

  const formatStartDate = (startDate: string) => {
    const parsedDate = parseISO(startDate)
    return isValid(parsedDate) ? format(parsedDate, 'MMMM yyyy') : ''
  }

  const monthDropdownOptions = [...careOverviewInsights]
    .sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime())
    .map((insight) => formatStartDate(insight.start_date))
    .filter(Boolean)

  const careOverviewInsightsForSelectedMonthYear = careOverviewInsights?.find(
    (insight) => formatStartDate(insight.start_date) === selectedMonthYear,
  )

  return (
    <>
      <CareInsightsOnboardingModal isModalOpen={isOnboardingModalOpen} onClose={handleCloseOnboardingModal} />
      <SupervisorUsageGuidelinesModal
        isModalOpen={isSupervisorUsageGuidelinesModalOpen}
        onClose={handleCloseSupervisorUsageGuidelinesModal}
      />
      {isSkillsGlossaryEnabled && (
        <SkillsGlossarySidePanel
          visible={isSkillsGlossaryOpen}
          onClose={closeSkillsGlossary}
          entries={skillsGlossaryEntries}
          activeSubAreaName={activeGlossarySkill}
        />
      )}
      <TitleContainer testID={tID('CareInsights-titleContainer')}>
        <Title
          size={TitleSize.LARGE}
          text={formatMessage(
            {
              defaultMessage: 'Monthly care insights for {providerName}',
              description: 'Header for Care Insights Tab in Caseload',
            },
            { providerName: `${user?.first_name} ${user?.last_name}` },
          )}
        />
      </TitleContainer>
      <ContentContainer>
        {!isLoadingProviderInsights && emptyClinicalQualitySectionCount > 0 && (
          <InlineWarningBanner
            testId='CareInsights-sessionRecordingsBanner'
            title={formatMessage({
              defaultMessage: 'Unlock more insights with session recordings',
              description: 'Title for Care Insights session recordings banner',
            })}
            text={formatMessage({
              defaultMessage:
                'Full insights require 5 recorded sessions. As additional clients consent to record sessions, these deeper analyses will automatically appear in your future reports.',
              description: 'Body text for Care Insights session recordings banner',
            })}
            marginTop='24px'
            marginBottom='0px'
            textColor={colors.inputTextWarning}
            textSize={BodyTextSize.SMALL}
          />
        )}
        <SubHeaderContainer>
          <MonthSelectorContainer>
            <Title size={TitleSize.XLARGE} text={selectedMonthYear} testID={tID('CareInsights-subHeaderContainer')} />
            <MonthYearDropdownMenu
              options={monthDropdownOptions}
              onOptionSelected={(selected: string) => {
                setSelectedMonthYear(selected)
                track({
                  event: mixpanelEvents.BUTTON_PRESS,
                  action: actions.CARE_INSIGHTS_SELECT_MONTH,
                  page: mixpanelPages.CARE_INSIGHTS,
                  details: { month: format(parse(selected, 'MMMM yyyy', new Date()), 'yyyy-MM'), provider_id: userId },
                })
              }}
              selectedOption={selectedMonthYear}
            />
          </MonthSelectorContainer>
          {isSkillsGlossaryEnabled && (
            <Link
              testID={tID('CareInsights-skillsGlossaryButton')}
              text={formatMessage({
                defaultMessage: 'Skills glossary',
                description: 'Link that opens the skills glossary side panel in Care Insights',
              })}
              onPress={() => openSkillsGlossary()}
              size={BodyTextSize.SMALL}
            />
          )}
        </SubHeaderContainer>
        <InsightsContainer>
          <CareInsightsOverview
            careOverviewInsightsForSelectedMonthYear={careOverviewInsightsForSelectedMonthYear}
            isLoadingProviderInsights={isLoadingProviderInsights}
            emptyClinicalQualitySectionCount={emptyClinicalQualitySectionCount}
            onLearnMorePress={() => {
              setIsOnboardingModalOpen(true)
              track({
                event: mixpanelEvents.BUTTON_PRESS,
                action: actions.CARE_INSIGHTS_LEARN_MORE,
                page: mixpanelPages.CARE_INSIGHTS,
              })
            }}
            totalClinicalQualitySections={totalClinicalQualitySections}
          />
          {showRiskManagementInCareInsights && (
            <RiskManagementInsights
              aiInsightsCurrentMonth={
                providerInsightsCurrentMonth?.summary?.[ProviderInsightsCompetencyCategory.RISK_MANAGEMENT]
              }
              aiInsightsPastMonth={
                providerInsightsPastMonth?.summary?.[ProviderInsightsCompetencyCategory.RISK_MANAGEMENT]
              }
              getClientInfoFromIds={getClientInfoFromIds}
              isLoadingClients={isLoadingClients}
              isLoadingProviderInsights={isLoadingProviderInsights}
              isSupervisor={isSupervisor}
              onClientPress={navigateToClientProfile}
              onSupervisorUsageGuidelinesPress={() => setIsSupervisorUsageGuidelinesModalOpen(true)}
            />
          )}
          <TreatmentPlanningInsights
            aiInsightsCurrentMonth={
              providerInsightsCurrentMonth?.summary?.[ProviderInsightsCompetencyCategory.TREATMENT_PLANNING]
            }
            aiInsightsPastMonth={
              providerInsightsPastMonth?.summary?.[ProviderInsightsCompetencyCategory.TREATMENT_PLANNING]
            }
            getClientInfoFromIds={getClientInfoFromIds}
            isLoadingClients={isLoadingClients}
            isLoadingProviderInsights={isLoadingProviderInsights}
            isSupervisor={isSupervisor}
            onClientPress={navigateToClientProfile}
            onSkillPress={isSkillsGlossaryEnabled ? openSkillsGlossary : undefined}
            onSupervisorUsageGuidelinesPress={() => setIsSupervisorUsageGuidelinesModalOpen(true)}
            metricInsights={careInsightsMetrics}
            isLoadingCareInsightsMetrics={isLoadingCareInsightsMetrics}
          />
          <CulturallyResponsiveCareInsights
            aiInsightsCurrentMonth={
              providerInsightsCurrentMonth?.summary?.[ProviderInsightsCompetencyCategory.CULTURALLY_RESPONSIVE]
            }
            aiInsightsPastMonth={
              providerInsightsPastMonth?.summary?.[ProviderInsightsCompetencyCategory.CULTURALLY_RESPONSIVE]
            }
            getClientInfoFromIds={getClientInfoFromIds}
            isLoadingClients={isLoadingClients}
            isLoadingProviderInsights={isLoadingProviderInsights}
            isSupervisor={isSupervisor}
            onClientPress={navigateToClientProfile}
            onSkillPress={isSkillsGlossaryEnabled ? openSkillsGlossary : undefined}
            onSupervisorUsageGuidelinesPress={() => setIsSupervisorUsageGuidelinesModalOpen(true)}
            metricInsights={careInsightsMetrics}
            isLoadingCareInsightsMetrics={isLoadingCareInsightsMetrics}
          />
          <TherapeuticRelationshipInsights
            aiInsightsCurrentMonth={
              providerInsightsCurrentMonth?.summary?.[ProviderInsightsCompetencyCategory.ATTUNEMENT_AND_RAPPORT]
            }
            aiInsightsPastMonth={
              providerInsightsPastMonth?.summary?.[ProviderInsightsCompetencyCategory.ATTUNEMENT_AND_RAPPORT]
            }
            getClientInfoFromIds={getClientInfoFromIds}
            isLoadingClients={isLoadingClients}
            isLoadingProviderInsights={isLoadingProviderInsights}
            isSupervisor={isSupervisor}
            onClientPress={navigateToClientProfile}
            onSkillPress={isSkillsGlossaryEnabled ? openSkillsGlossary : undefined}
            onSupervisorUsageGuidelinesPress={() => setIsSupervisorUsageGuidelinesModalOpen(true)}
            metricInsights={careInsightsMetrics}
            isLoadingCareInsightsMetrics={isLoadingCareInsightsMetrics}
          />
          <SessionAdministrationInsights
            isSupervisor={isSupervisor}
            onSupervisorUsageGuidelinesPress={() => setIsSupervisorUsageGuidelinesModalOpen(true)}
            metricInsights={careInsightsMetrics}
            isLoadingCareInsightsMetrics={isLoadingCareInsightsMetrics}
          />
        </InsightsContainer>
        <CareInsightsFeedbackSection selectedMonthYear={selectedMonthYear} isSupervisor={isSupervisor} />
      </ContentContainer>
    </>
  )
}

export default CareInsights
